<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { NInput, NButton } from 'naive-ui';
import { LogIn, UserPlus, Key, Globe2, ExternalLink, AlertCircle, ShieldAlert, ShieldCheck } from 'lucide-vue-next';
import { API_BASE_URL, CLERK_PUBLISHABLE_KEY, setAuthToken } from '@/services/api';
import { clerkService } from '@/services/clerkService';
import { useTenant, hasActiveSubscription } from '@/composables/useTenant';
import { useToastNotification } from '@/composables/useToastNotification';
import SocialAuthButtons from '@/components/auth/SocialAuthButtons.vue';

const { t } = useI18n();
const router = useRouter();
const { setPersona, refreshData } = useTenant();
const { notifySuccess, notifyError, notifyInfo } = useToastNotification();

const email = ref('');
const password = ref('');
const isLoggingIn = ref(false);
const clerkErrorMessage = ref<string | null>(null);
const needsClientTrust = ref(false);

const hasClerkKey = computed(() => Boolean(CLERK_PUBLISHABLE_KEY));

let unsubscribeClerk: (() => void) | null = null;

onMounted(async () => {
  // Listen for active Clerk session updates (e.g., when verified via Clerk Modal/Turnstile)
  unsubscribeClerk = await clerkService.onClerkSessionChange(async (session, user) => {
    console.info('[LoginView] Detected active session from Clerk listener:', user?.id);
    try {
      const token = await session.getToken();
      if (token) setAuthToken(token);
    } catch (e) {}

    const fullName = user.fullName || user.firstName || email.value.split('@')[0] || 'User';
    setPersona({
      id: user.id,
      fullName,
      email: user.primaryEmailAddress?.emailAddress || email.value,
      role: 'ADMIN',
      avatarUrl: user.imageUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80',
    });

    await refreshData();
    notifySuccess(t('auth.authComplete'), t('auth.welcomeBack', { name: fullName }));
    if (!hasActiveSubscription.value) {
      router.push('/dashboard/billing');
    } else {
      router.push('/dashboard');
    }
  });
});

onUnmounted(() => {
  if (unsubscribeClerk) unsubscribeClerk();
});

const handleLogin = async () => {
  if (!email.value || !password.value) {
    notifyError(t('auth.missingCreds'), t('auth.missingCredsMsg'));
    return;
  }

  isLoggingIn.value = true;
  clerkErrorMessage.value = null;
  needsClientTrust.value = false;

  try {
    notifyInfo(t('auth.authenticating'), t('auth.connectingToClerk', { email: email.value }));
    const clerkResult = await clerkService.signIn(email.value, password.value);
    
    if (!clerkResult.success) {
      if (clerkResult.code === 'needs_client_trust' || clerkResult.status === 'needs_client_trust') {
        needsClientTrust.value = true;
        clerkErrorMessage.value = clerkResult.error || 'Client trust verification required by Clerk.';
        notifyInfo(t('auth.verificationRequired'), t('auth.verificationChallenge'));
      } else {
        clerkErrorMessage.value = clerkResult.error || 'Authentication failed.';
        notifyError(t('auth.authFailed'), clerkResult.error || t('auth.couldNotSignIn'));
      }
      isLoggingIn.value = false;
      return;
    }

    if (clerkResult.token) {
      setAuthToken(clerkResult.token);
    }

    const userId = clerkResult.user?.id || `usr_clerk_${Date.now()}`;
    const fullName = clerkResult.user?.fullName || clerkResult.user?.firstName || email.value.split('@')[0];

    setPersona({
      id: userId,
      fullName: fullName || 'Tenant Administrator',
      email: email.value,
      role: 'ADMIN',
      avatarUrl: clerkResult.user?.imageUrl || `https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80`,
    });

    // Hydrate backend API data now that user is authenticated
    await refreshData();

    notifySuccess(t('auth.authSuccess'), t('auth.welcomeBack', { name: fullName }));
    if (!hasActiveSubscription.value) {
      notifyInfo(t('auth.subRequired'), t('auth.subRequiredMsg'));
      router.push('/dashboard/billing');
    } else {
      router.push('/dashboard');
    }
  } catch (err: any) {
    console.error('Login Error:', err);
    clerkErrorMessage.value = err.message || 'Authentication failed';
    notifyError(t('auth.authFailed'), err.message || t('auth.couldNotSignIn'));
  } finally {
    isLoggingIn.value = false;
  }
};

const openClerkNativeModal = async () => {
  const success = await clerkService.openNativeSignIn(email.value || undefined);
  if (!success) {
    notifyInfo(
      'Clerk Modal',
      'Clerk UI modal is loading or unavailable in this view.'
    );
  }
};

const handleSocialAuth = async (provider: 'google' | 'apple' | 'line', profile?: { name: string; email: string }) => {
  if (profile) {
    email.value = profile.email;
    const userId = `usr_${provider}_${Date.now()}`;
    setPersona({
      id: userId,
      fullName: profile.name,
      email: profile.email,
      role: 'ADMIN',
      avatarUrl: `https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80`,
    });
    setAuthToken(`usr_token_${userId}_${Date.now()}`);
    await refreshData();
    if (!hasActiveSubscription.value) {
      router.push('/dashboard/billing');
    } else {
      router.push('/dashboard');
    }
  }
};
</script>

<template>
  <div class="space-y-6">
    <div class="text-center">
      <h2 class="text-xl font-extrabold text-slate-100 tracking-tight">{{ t('auth.signInTitle') }}</h2>
      <p class="text-xs text-slate-400 mt-1">
        {{ t('auth.signInSubtitle') }}
      </p>
    </div>

    <!-- Active Environment Integration Badge -->
    <div class="p-3 bg-slate-950/80 border border-slate-800 rounded-2xl space-y-1.5 text-xs">
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-1.5 text-slate-400 font-medium">
          <Key class="w-3.5 h-3.5 text-sky-400" />
          {{ t('auth.clerkAuth') }}
        </span>
        <span
          :class="[
            'font-mono text-[10px] px-2 py-0.5 rounded font-bold',
            hasClerkKey ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-slate-800 text-slate-400'
          ]"
        >
          {{ hasClerkKey ? 'VITE_CLERK_PUBLISHABLE_KEY Active' : 'Clerk Integrated' }}
        </span>
      </div>

      <div class="flex items-center justify-between">
        <span class="flex items-center gap-1.5 text-slate-400 font-medium">
          <Globe2 class="w-3.5 h-3.5 text-sky-400" />
          {{ t('auth.apiTarget') }}
        </span>
        <span class="font-mono text-[10px] text-sky-300 truncate max-w-[160px]" :title="API_BASE_URL">
          {{ API_BASE_URL }}
        </span>
      </div>
    </div>

    <!-- Client Trust Verification Banner -->
    <div v-if="needsClientTrust" class="p-4 bg-sky-950/60 border border-sky-500/40 rounded-2xl text-xs text-sky-200 space-y-3 animate-in fade-in slide-in-from-top-2">
      <div class="flex items-start gap-2.5">
        <ShieldAlert class="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
        <div>
          <p class="font-bold text-sky-300 text-sm">{{ t('auth.clientTrustTitle') }}</p>
          <p class="mt-1 text-[11px] text-slate-300 leading-relaxed">
            {{ t('auth.clientTrustDesc') }}
          </p>
        </div>
      </div>
      <div class="pt-2 border-t border-sky-500/20 flex flex-col sm:flex-row items-center justify-between gap-2">
        <span class="text-[10px] text-slate-400 flex items-center gap-1">
          <ShieldCheck class="w-3.5 h-3.5 text-emerald-400" />
          {{ t('auth.dialogNotShowing') }}
        </span>
        <NButton
          type="info"
          size="small"
          attr-type="button"
          @click="openClerkNativeModal"
          class="font-bold text-xs w-full sm:w-auto"
        >
          <template #icon><ExternalLink class="w-3.5 h-3.5" /></template>
          {{ t('auth.openVerificationModal') }}
        </NButton>
      </div>
    </div>

    <!-- Clerk Error Banner -->
    <div v-else-if="clerkErrorMessage" class="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-xs text-amber-200 space-y-2">
      <div class="flex items-start gap-2">
        <AlertCircle class="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p class="font-semibold text-amber-300">{{ t('auth.authNotice') }}</p>
          <p class="mt-0.5 text-[11px] text-slate-300">{{ clerkErrorMessage }}</p>
        </div>
      </div>
      <div class="pt-2 border-t border-amber-500/20 flex items-center justify-between">
        <router-link to="/register-tenant" class="font-bold text-amber-400 hover:text-amber-300 underline">
          {{ t('auth.registerNewAccount') }}
        </router-link>
      </div>
    </div>

    <!-- Social Logins (Google, Apple, LINE) -->
    <SocialAuthButtons mode="login" @social-auth="handleSocialAuth" />

    <!-- Email/Password Login Form -->
    <form @submit.prevent="handleLogin" class="space-y-4">
      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('auth.emailLabel') }}</label>
        <NInput
          v-model:value="email"
          type="text"
          :placeholder="t('auth.emailPlaceholder')"
          size="medium"
          required
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('auth.passwordLabel') }}</label>
        <NInput
          v-model:value="password"
          type="password"
          show-password-on="click"
          :placeholder="t('auth.passwordPlaceholder')"
          size="medium"
          required
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <NButton
          type="primary"
          size="large"
          attr-type="submit"
          :loading="isLoggingIn"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon>
            <LogIn class="w-4 h-4" />
          </template>
          {{ t('auth.signInBtn') }}
        </NButton>

        <NButton
          size="large"
          attr-type="button"
          @click="openClerkNativeModal"
          class="font-semibold text-xs"
        >
          <template #icon>
            <ExternalLink class="w-4 h-4 text-sky-400" />
          </template>
          {{ t('auth.clerkModalBtn') }}
        </NButton>
      </div>

      <!-- Clerk CAPTCHA Container - Mounted directly beneath Sign In button -->
      <div id="clerk-captcha" class="empty:hidden flex justify-center items-center my-2.5 w-full min-h-0"></div>

      <div class="pt-3 border-t border-slate-800 flex flex-col gap-2 text-center text-xs">
        <router-link to="/register-tenant" class="text-sky-400 hover:text-sky-300 font-bold flex items-center justify-center gap-1.5">
          <UserPlus class="w-3.5 h-3.5" />
          <span>{{ t('auth.registerNewOrg') }}</span>
        </router-link>
      </div>
    </form>
  </div>
</template>
